import { Button, Card, Title2, tokens } from '@fluentui/react-components';
import { Checkmark16Filled, Dismiss16Filled } from '@fluentui/react-icons';
import { Fragment, useMemo, useState } from 'react';

import type { EvaluationMetric, ScoreClass } from '../../../types/debug';
import type { EvalStats } from '../../../types/evalStats';
import {
  getScaleFraction,
  resolveScaleFraction,
  resolveScoreClass,
  resolveScoreDenominator,
} from '../../../utils/evalStatsClassifier';
import { formatMetricName } from '../../../utils/sigsParser';

import type { EvalMetricsGridProps } from './EvalMetricsGrid.interface';
import { useStyles } from './EvalMetricsGrid.style';

function scoreColor(cls: ScoreClass): string {
  switch (cls) {
    case 'good':
    case 'pass':
      return tokens.colorPaletteGreenForeground1;
    case 'fair':
      return tokens.colorPaletteYellowForeground1;
    case 'poor':
    case 'fail':
      return tokens.colorPaletteRedForeground1;
    default:
      return tokens.colorNeutralForeground3;
  }
}

/** Sentinel score meaning "not evaluated / not applicable". */
const NOT_APPLICABLE_SCORE = -1;

/** A metric paired with its resolved display values, ready to render. */
interface MetricRowData {
  /**
   * Identity of this row for React keys and expand/collapse state. A single
   * interaction can carry the *same* metric name more than once (one emission
   * per evaluator instance, e.g. two `ClaimGroundedness` families), so the name
   * alone is not unique — the emission index disambiguates it.
   */
  rowKey: string;
  metric: EvaluationMetric;
  stats: EvalStats | undefined;
  effectiveScore: number;
  countLabel?: string;
  /**
   * True for metrics that are a raw count used only as context (e.g.
   * `ClaimGroundedness_Total`, the number of claims a response made). These are
   * the denominator of a ratio metric, not a graded score, so they render as a
   * plain informational count rather than an `x/max` score with a bar.
   */
  isCount?: boolean;
}

interface ResolvedScore {
  /**
   * The value used for classification, the score bar and the distribution
   * track. For ordinary metrics this is the raw score; for ratio metrics it is
   * the `count / denominator` ratio so it lines up with the ratio-based stats.
   */
  effectiveScore: number;
  /**
   * For ratio metrics, the literal `count/total` string to display instead of
   * the usual `score/max`. Undefined for ordinary metrics.
   */
  countLabel?: string;
}

/**
 * Finds the denominator metric for the count metric at `index`.
 *
 * A single interaction can carry the same evaluator family more than once (e.g.
 * two `ClaimGroundedness` emissions, each with its own `ClaimGroundedness_Total`
 * over a different claim set). Looking the denominator up by name alone would
 * collapse those duplicates onto whichever emission happened to win, dividing
 * one family's counts by the other family's total — so `3` supported out of `5`
 * claims renders as `3/9`.
 *
 * Metrics arrive in emission order, so each family is contiguous and the
 * numerators precede their own `*_Total`. We therefore take the *first*
 * denominator at or after the metric, which always stays inside the current
 * emission. Searching outward by absolute distance instead would pick the
 * previous emission's total whenever the metric sits closer to it — e.g. the
 * second `_Supported` in
 * `[Supported1, Unsupported1, Total1, Supported2, Unsupported2, Total2]`.
 *
 * The backward pass is only a fallback for the unexpected ordering where a
 * payload places the total ahead of its numerators.
 */
function findDenominator(
  metrics: readonly EvaluationMetric[],
  index: number,
  denominatorName: string,
): EvaluationMetric | undefined {
  for (let i = index; i < metrics.length; i++) {
    const candidate = metrics[i];
    if (candidate?.evaluationName === denominatorName) return candidate;
  }
  for (let i = index - 1; i >= 0; i--) {
    const candidate = metrics[i];
    if (candidate?.evaluationName === denominatorName) return candidate;
  }
  return undefined;
}

/**
 * Resolves how a metric's score should be interpreted for display.
 *
 * Count-based metrics (e.g. `ClaimGroundedness_Supported`) carry a raw count
 * whose historical stats are stored as a `count / total` ratio. We convert the
 * live count to that ratio using this response's own denominator metric (named
 * by `stats.denominatorEval`) so it can be ranked against the ratio
 * distribution, while still surfacing the literal `count/total` to the user.
 * When the denominator is missing or zero (no claims), the metric is treated as
 * not applicable.
 *
 * Ratio metrics (`stats.kind === 'ratio'`) with no denominator already carry a
 * continuous score in [0, 1] (e.g. the fraction of claims supported), so no
 * conversion is needed — the raw score is the ratio, surfaced as a percentage.
 *
 * @param metric The metric to resolve.
 * @param stats The metric's historical statistics, if any.
 * @param metrics All metrics on the interaction, in emission order.
 * @param index The position of `metric` within `metrics`.
 */
export function resolveScore(
  metric: EvaluationMetric,
  stats: EvalStats | undefined,
  metrics: readonly EvaluationMetric[],
  index: number,
): ResolvedScore {
  // `denominatorEval` is optional and arrives as JSON `null` from the API when
  // absent, so treat null/undefined/empty all as "no denominator".
  const denominatorName: string | null | undefined = stats?.denominatorEval;
  if (
    denominatorName === undefined ||
    denominatorName === null ||
    denominatorName === ''
  ) {
    if (stats?.kind === 'ratio') {
      return {
        effectiveScore: metric.score,
        countLabel: `${Math.round(metric.score * 100).toString()}%`,
      };
    }
    return { effectiveScore: metric.score };
  }

  const denominator = findDenominator(metrics, index, denominatorName);
  if (denominator === undefined || denominator.score <= 0) {
    return { effectiveScore: NOT_APPLICABLE_SCORE };
  }

  return {
    effectiveScore: metric.score / denominator.score,
    countLabel: `${metric.score.toString()}/${denominator.score.toString()}`,
  };
}

/**
 * A compact visualization of where a score falls within the metric's historical
 * distribution: a track spanning [min, max] with percentile tick marks and a
 * marker for the current score.
 */
function MetricDistribution({
  score,
  markerLabel,
  cls,
  stats,
}: {
  score: number;
  markerLabel: string;
  cls: ScoreClass;
  stats: EvalStats;
}) {
  const styles = useStyles();
  const color = scoreColor(cls);
  const scorePosition = getScaleFraction(score, stats) * 100;

  // Group percentiles that share a value so overlapping ticks/labels collapse
  // into a single, readable mark (e.g. "P50 P95" when both land on 5).
  const ticks = useMemo(() => {
    const byValue = new Map<number, number[]>();
    for (const p of stats.percentiles) {
      const existing = byValue.get(p.value) ?? [];
      existing.push(p.percentile);
      byValue.set(p.value, existing);
    }
    return Array.from(byValue.entries()).map(([value, percentiles]) => ({
      value,
      position: getScaleFraction(value, stats) * 100,
      label: percentiles.map((p) => `P${p.toString()}`).join(' '),
    }));
  }, [stats]);

  return (
    <div className={styles.distribution}>
      <div className={styles.distributionHeader}>
        <span>Historical distribution</span>
      </div>
      <div className={styles.track}>
        <div className={styles.trackLine} />
        {ticks.map((tick) => (
          <div
            key={tick.label}
            className={styles.tick}
            style={{ left: `${tick.position.toString()}%` }}
          >
            <span className={styles.tickLabel}>
              {tick.label} ({tick.value})
            </span>
          </div>
        ))}
        <div
          className={styles.scoreMarker}
          style={{
            left: `${scorePosition.toString()}%`,
            backgroundColor: color,
          }}
        >
          <span className={styles.scoreMarkerLabel} style={{ color }}>
            {markerLabel}
          </span>
        </div>
      </div>
      <div className={styles.trackScale}>
        <span>min {stats.scaleMin}</span>
        <span>max {stats.scaleMax}</span>
      </div>
    </div>
  );
}

function MetricRow({
  metric,
  stats,
  effectiveScore,
  countLabel,
  isCount = false,
  isExpanded,
  onToggle,
}: {
  metric: EvaluationMetric;
  stats: EvalStats | undefined;
  effectiveScore: number;
  countLabel?: string;
  isCount?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const styles = useStyles();
  const cls = resolveScoreClass(effectiveScore, metric.evaluationName, stats);
  const color = scoreColor(cls);

  // Count metrics are contextual (how many claims were made), not scored, so
  // they never get a scale bar, colour or distribution.
  const isScaled =
    !isCount && cls !== 'not-applicable' && cls !== 'pass' && cls !== 'fail';
  const fillFraction = resolveScaleFraction(effectiveScore, stats);
  const denominator = resolveScoreDenominator(stats);

  return (
    <>
      <div
        className={styles.metricRow}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <span className={styles.metricLabel}>
          {formatMetricName(metric.evaluationName)}
        </span>

        {isScaled && (
          <div className={styles.scoreBar}>
            <div
              className={styles.scoreBarFill}
              style={{
                width: `${(fillFraction * 100).toString()}%`,
                backgroundColor: color,
              }}
            />
          </div>
        )}

        {!isScaled && <div style={{ flex: 1 }} />}

        <span className={styles.metricValue}>
          {isCount ? (
            <span style={{ color: tokens.colorNeutralForeground2 }}>
              {metric.score.toString()}
            </span>
          ) : cls === 'not-applicable' ? (
            <span style={{ color: tokens.colorNeutralForeground3 }}>
              &mdash;
            </span>
          ) : cls === 'pass' || cls === 'fail' ? (
            <span style={{ color }}>
              {cls === 'pass' ? (
                <>
                  <Checkmark16Filled /> Pass
                </>
              ) : (
                <>
                  <Dismiss16Filled /> Fail
                </>
              )}
            </span>
          ) : (
            <span style={{ color }}>
              {countLabel ??
                `${metric.score.toString()}/${denominator.toString()}`}
            </span>
          )}
        </span>
      </div>
      {isExpanded && (
        <>
          {isScaled && stats && (
            <MetricDistribution
              score={effectiveScore}
              markerLabel={countLabel ?? metric.score.toString()}
              cls={cls}
              stats={stats}
            />
          )}
          {metric.explanation && (
            <div className={styles.explanation}>{metric.explanation}</div>
          )}
        </>
      )}
    </>
  );
}

export function EvalMetricsGrid({ data, evalStats }: EvalMetricsGridProps) {
  const styles = useStyles();
  const [expandedMetrics, setExpandedMetrics] = useState<Set<string>>(
    new Set(),
  );

  const toggleMetric = (name: string) => {
    setExpandedMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const metricRowKeys = useMemo(
    () =>
      data.evaluationMetrics.map(
        (m, index) => `${m.evaluationName}#${index.toString()}`,
      ),
    [data.evaluationMetrics],
  );

  const grouped = useMemo(() => {
    const scaled: MetricRowData[] = [];
    const ratio: MetricRowData[] = [];
    const binary: MetricRowData[] = [];
    const info: MetricRowData[] = [];
    const na: MetricRowData[] = [];

    data.evaluationMetrics.forEach((m, index) => {
      const stats = evalStats?.get(m.evaluationName);
      const { effectiveScore, countLabel } = resolveScore(
        m,
        stats,
        data.evaluationMetrics,
        index,
      );
      const isCount = stats?.kind === 'count';
      const cls = resolveScoreClass(effectiveScore, m.evaluationName, stats);
      const row: MetricRowData = {
        rowKey: `${m.evaluationName}#${index.toString()}`,
        metric: m,
        stats,
        effectiveScore,
        countLabel,
        isCount,
      };
      // Group by the metric's `kind` when it has stats; fall back to the
      // classification for unseeded metrics (e.g. an eval from an agent not yet
      // in the seed) so their legacy pass/fail vs graded split is preserved.
      // Ratio metrics (a fraction in [0, 1], e.g. groundedness) get their own
      // section so they aren't visually conflated with fixed-point scaled
      // scores (e.g. 1-5).
      if (isCount) {
        info.push(row);
      } else if (cls === 'not-applicable') {
        na.push(row);
      } else if (stats?.kind === 'binary' || cls === 'pass' || cls === 'fail') {
        binary.push(row);
      } else if (stats?.kind === 'ratio') {
        ratio.push(row);
      } else {
        scaled.push(row);
      }
    });

    return { scaled, ratio, binary, info, na };
  }, [data.evaluationMetrics, evalStats]);

  if (data.evaluationMetrics.length === 0) {
    return (
      <Card className={styles.root}>
        <Title2 className={styles.header}>Evaluation Metrics</Title2>
        <div className={styles.emptyState}>
          No evaluation metrics available. This interaction may not have been
          evaluated.
        </div>
      </Card>
    );
  }

  return (
    <Card className={styles.root}>
      <div className={styles.headerRow}>
        <Title2 className={styles.header}>Evaluation Metrics</Title2>
        <Button
          appearance="subtle"
          size="small"
          onClick={() => {
            if (expandedMetrics.size === metricRowKeys.length) {
              setExpandedMetrics(new Set());
            } else {
              setExpandedMetrics(new Set(metricRowKeys));
            }
          }}
        >
          {expandedMetrics.size === metricRowKeys.length
            ? 'Collapse All'
            : 'Expand All'}
        </Button>
      </div>

      {(
        [
          ['Scaled Metrics', grouped.scaled],
          ['Ratio Metrics', grouped.ratio],
          ['Binary Metrics', grouped.binary],
          ['Informational', grouped.info],
          ['Not Evaluated', grouped.na],
        ] as const
      ).map(([label, rows]) =>
        rows.length === 0 ? null : (
          <Fragment key={label}>
            <span className={styles.groupLabel}>
              {label} ({rows.length})
            </span>
            <div>
              {rows.map((row) => (
                <MetricRow
                  key={row.rowKey}
                  metric={row.metric}
                  stats={row.stats}
                  effectiveScore={row.effectiveScore}
                  countLabel={row.countLabel}
                  isCount={row.isCount}
                  isExpanded={expandedMetrics.has(row.rowKey)}
                  onToggle={() => {
                    toggleMetric(row.rowKey);
                  }}
                />
              ))}
            </div>
          </Fragment>
        ),
      )}
    </Card>
  );
}
